import React from 'react';
import { cn } from '../../utils/cn';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
    period?: string;
  };
  icon?: React.ReactNode;
  trend?: number[];
  className?: string;
  loading?: boolean;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  icon,
  trend,
  className,
  loading = false,
}) => {
  if (loading) {
    return (
      <div
        className={cn(
          'relative p-6 rounded-2xl bg-slate-900/40 border border-white/5 backdrop-blur-xl overflow-hidden',
          className
        )}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-slate-800 rounded w-1/3"></div>
          <div className="h-10 bg-slate-800 rounded w-1/2"></div>
          <div className="h-4 bg-slate-800/50 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  const getChangeColor = (type: 'increase' | 'decrease' | 'neutral') => {
    switch (type) {
      case 'increase':
        return 'text-emerald-400 bg-emerald-400/10 border border-emerald-400/20';
      case 'decrease':
        return 'text-rose-400 bg-rose-400/10 border border-rose-400/20';
      default:
        return 'text-slate-400 bg-slate-400/10 border border-slate-400/20';
    }
  };

  const getChangeIcon = (type: 'increase' | 'decrease' | 'neutral') => {
    switch (type) {
      case 'increase':
        return (
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        );
      case 'decrease':
        return (
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        );
      default:
        return (
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        );
    }
  };

  // Find max for trend scaling safely, avoid division by zero
  const maxTrend = trend && trend.length > 0 ? Math.max(...trend, 1) : 1;

  return (
    <div
      className={cn(
        'group relative p-6 rounded-2xl bg-gradient-to-br from-slate-900/80 to-slate-800/40 border border-white/5 backdrop-blur-xl overflow-hidden hover:border-white/10 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300',
        className
      )}
    >
      {/* Dynamic Background Glow on Hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/10 group-hover:to-indigo-500/10 transition-colors duration-500" />

      <div className="relative z-10 flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-400 tracking-wide uppercase">
          {title}
        </h3>
        {icon && (
          <div className="p-2 rounded-lg bg-white/5 border border-white/5 text-slate-300 group-hover:text-blue-400 group-hover:scale-110 transition-all duration-300">
            {icon}
          </div>
        )}
      </div>

      <div className="relative z-10 flex items-end space-x-3 mb-1">
        <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-200">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </span>
      </div>

      <div className="relative z-10 min-h-[28px] flex items-center mt-2">
        {change ? (
          <div className="flex items-center space-x-2">
            <span
              className={cn(
                'inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-semibold backdrop-blur-sm',
                getChangeColor(change.type)
              )}
            >
              <span>{getChangeIcon(change.type)}</span>
              <span>{Math.abs(change.value)}%</span>
            </span>
            {change.period && (
              <span className="text-xs text-slate-500 font-medium">
                vs last {change.period}
              </span>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-600 font-medium">
            No recent changes
          </div>
        )}
      </div>

      {trend && trend.length > 0 && (
        <div className="relative z-10 flex items-end space-x-1 h-10 mt-4 group">
          {trend.map((point, index) => {
            const height = `${(point / maxTrend) * 100}%`;
            return (
              <div
                key={index}
                className="relative flex-1 bg-gradient-to-t from-blue-600/30 to-blue-400/50 rounded-sm opacity-60 hover:opacity-100 hover:from-blue-500 hover:to-indigo-400 transition-all cursor-pointer group-hover:h-[calc(100%+4px)] duration-300"
                style={{ height }}
              >
                {/* Sparkline Tooltip effect could be added here */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
